import * as THREE from 'three';

/** How many sparks can fly at once across the whole HUD. */
const POOL = 240;

/** How a burst of sparks flies: its colour, how many, how fast (CSS pixels a second), which way and how widely, and how long they live. */
export interface SparkBurst {
  color: THREE.ColorRepresentation;
  count: number;
  speed: number;
  /** The direction they fly (radians, 0 = right, anticlockwise) and the spread either side of it; a full circle when omitted. */
  angle?: number; spread?: number;
  /** Seconds a spark lives, before a little random variation. */
  life?: number;
  /** Pull downward in CSS pixels a second squared: sparks fall, embers barely. */
  gravity?: number;
}

/**
 * Sparks and embers for the 3D HUD: hot points of light that fly from a struck piece, arc under their own weight, cool from white
 * through their colour to nothing, and are gone. One pool serves every piece; a burst past its size reuses the oldest sparks.
 * Positions are the HUD's CSS pixels; it draws additively, on top of the pieces.
 */
export class HudSparks extends THREE.Points<THREE.BufferGeometry, THREE.PointsMaterial> {
  private positions = new Float32Array(POOL * 3);
  private colors = new Float32Array(POOL * 3);
  private velocity = new Float32Array(POOL * 2);
  private life = new Float32Array(POOL);
  private span = new Float32Array(POOL);
  private gravity = new Float32Array(POOL);
  private hue = Array.from({ length: POOL }, () => new THREE.Color());
  private next = 0;
  private hot = new THREE.Color(0xfff4dc);
  private mixed = new THREE.Color();
  /** The HUD's scale (interface size), so sparks grow with the pieces. */
  zoom = 1;

  constructor(glow: THREE.Texture | null) {
    const geometry = new THREE.BufferGeometry();
    super(geometry, new THREE.PointsMaterial({ size: 7, map: glow, vertexColors: true, sizeAttenuation: false, transparent: true, depthWrite: false, depthTest: false, blending: THREE.AdditiveBlending }));
    geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3).setUsage(THREE.DynamicDrawUsage));
    geometry.setAttribute('color', new THREE.BufferAttribute(this.colors, 3).setUsage(THREE.DynamicDrawUsage));
    this.frustumCulled = false; this.renderOrder = 10;
    this.raycast = () => {};
  }

  /** Throws a burst of sparks from a point in HUD pixels. */
  emit(x: number, y: number, burst: SparkBurst) {
    const spread = burst.spread ?? Math.PI, angle = burst.angle ?? 0;
    for (let n = 0; n < burst.count; n++) {
      const i = this.next; this.next = (this.next + 1) % POOL;
      const a = angle + (Math.random() * 2 - 1) * spread, speed = burst.speed * this.zoom * (.35 + Math.random() * .9);
      this.positions[i * 3] = x; this.positions[i * 3 + 1] = y; this.positions[i * 3 + 2] = 120;
      this.velocity[i * 2] = Math.cos(a) * speed; this.velocity[i * 2 + 1] = Math.sin(a) * speed;
      this.span[i] = this.life[i] = (burst.life ?? .6) * (.6 + Math.random() * .8);
      this.gravity[i] = (burst.gravity ?? 260) * this.zoom;
      this.hue[i].set(burst.color);
    }
  }

  update(dt: number) {
    this.material.size = 7 * this.zoom;
    // With every spark gone there is nothing to draw or upload.
    let alive = false;
    for (let i = 0; i < POOL; i++) if (this.life[i] > 0) { alive = true; break; }
    this.visible = alive;
    if (!alive) return;
    for (let i = 0; i < POOL; i++) {
      if (this.life[i] <= 0) { this.colors[i * 3] = this.colors[i * 3 + 1] = this.colors[i * 3 + 2] = 0; continue; }
      this.life[i] -= dt;
      // Air drags them, gravity bends them down.
      this.velocity[i * 2] *= 1 - Math.min(1, dt * 2.2); this.velocity[i * 2 + 1] = this.velocity[i * 2 + 1] * (1 - Math.min(1, dt * 2.2)) - this.gravity[i] * dt;
      this.positions[i * 3] += this.velocity[i * 2] * dt; this.positions[i * 3 + 1] += this.velocity[i * 2 + 1] * dt;
      // White-hot at first, then their own colour, fading out as they cool.
      const t = Math.max(0, this.life[i] / this.span[i]);
      this.mixed.copy(this.hue[i]).lerp(this.hot, Math.max(0, t - .6) * 2.5).multiplyScalar(t * t * 1.6);
      this.colors[i * 3] = this.mixed.r; this.colors[i * 3 + 1] = this.mixed.g; this.colors[i * 3 + 2] = this.mixed.b;
    }
    this.geometry.attributes.position.needsUpdate = true; this.geometry.attributes.color.needsUpdate = true;
  }

  dispose() { this.geometry.dispose(); this.material.dispose(); }
}
